<template>
  <div
    :class="cx('relative flex flex-col bg-[--v3-surface-surface-100] px-[16px] pb-[20px] pt-[60px]', $attrs.class)"
  >
    <div class="h-[64px] w-full">
      <img
        src="/img/logo.png"
        class="h-[25px]"
        alt="Erank Logo"
      >
    </div>
    <div class="flex h-fit flex-col justify-between gap-control md:h-[290px] md:flex-row md:gap-0">
      <div class="v3-body-regular-400-line-height-auto flex w-[319px] flex-col gap-[24px] text-[--v3-global-textsecondarycolor]">
        <span>{{ t('layout.copy_right', { year: new Date().getFullYear() }) }}</span>
        <p>
          {{ t('layout.affiliation') }}<br><br>
          {{ t('layout.etsy_disclaimer') }}
        </p>
        <span class="metadata text-[--v3-global-textcolor]">111-001 • 0.594 • 3 • 0.011</span>
      </div>
      <div class="flex flex-col gap-[60px] md:flex-row">
        <ClusterMenu :title="t('layout.company.title')" :items="company" @select="trackFooterLink($event.name)" />
        <ClusterMenu :title="t('layout.more_info.title')" :items="moreInfo" @select="trackFooterLink($event.name)" />
        <ClusterMenu :title="t('layout.legal.title')" :items="legal" @select="trackFooterLink($event.name)" />
      </div>
    </div>
    <div class="flex w-full justify-center md:justify-end">
      <div class="flex items-center gap-[20px]">
        <FooterSocialIcon
          v-for="{ url, icon, name } in socialLinks"
          :key="`footer-social-${icon}`"
          :url
          :icon
          @click="trackFooterLink(name)"
        />
      </div>
    </div>
    <div class="h-control w-full" />
  </div>
</template>

<script lang="ts" setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';

import { mixPanelEvents } from '@/common/constants';
import { useAuthStore } from '@/common/stores/auth';
import { track } from '@/helpers/analytics/mixpanel';
import { cx } from '@/helpers/cva';
import FooterSocialIcon from '@/modules/layout/components/atoms/FooterSocialIcon';
import ClusterMenu from '@/modules/layout/components/molecules/ClusterMenu';

const authStore = useAuthStore();
const { t } = useI18n();

// Dynamic plans URL based on referrer (same logic as UpgradeButton.vue)
const plansUrl = computed(() => {
  const referrer = authStore.user?.referrer;
  let redirect = '';
  switch (referrer) {
    case 'ad':
    case 'ad-sp':
      redirect = '/plans';
      break;
    case 'ad-reg':
      // Users from ad-reg should go directly to plans page
      redirect = '/plans';
      break;
    default:
      redirect = `/a/${referrer}`;
  }
  return authStore.redirectToReferralPage && referrer && referrer !== 'ad-reg' ? redirect : '/plans';
});

// `name` is the untranslated link label, so Mixpanel reports stay consistent across locales.
const company = [
  { url: 'https://help.erank.com/about/about-us', title: t('layout.company.about_us'), name: 'About Us' },
  { url: 'https://help.erank.com/about/contact-us', title: t('layout.company.contact_us'), name: 'Contact Us' },
];
const moreInfo = computed(() => [
  { url: plansUrl.value, title: t('layout.more_info.plans'), name: 'Plans' },
  { url: 'https://help.erank.com/features', title: t('layout.more_info.features'), name: 'Features' },
  { url: 'https://hc.erank.com/en/collections/3839043-getting-started', title: t('layout.more_info.beginners_guide'), name: 'Beginner\'s Guide' },
  { url: 'https://help.erank.com/blog', title: t('layout.more_info.blog'), name: 'Blog' },
  { url: 'https://erank.com/affiliates', title: t('layout.more_info.affiliates'), name: 'Affiliates' },
  { url: 'https://help.erank.com/faq', title: t('layout.more_info.faq'), name: 'FAQ' },
]);
const legal = [
  { url: 'https://public.erank.com/legal/privacy-policy', title: t('layout.legal.privacy_policy'), name: 'Privacy Policy' },
  { url: 'https://public.erank.com/legal/terms-of-service', title: t('layout.legal.terms_service'), name: 'Terms of Service' },
];
const socialLinks = [
  { url: 'https://facebook.com/groups/EtsyRank', icon: 'facebook', name: 'Facebook' },
  { url: 'https://instagram.com/erankseo/?hl=en', icon: 'instagram', name: 'Instagram' },
  { url: 'https://threads.com/@erankseo', icon: 'threads', name: 'Threads' },
  { url: 'https://youtube.com/c/eRank', icon: 'youtube', name: 'YouTube' },
];

const trackFooterLink = (name: string) => {
  track(mixPanelEvents.CLICKED_FOOTER_LINK, {
    'Link Name': name,
    'Erank Version': 3,
  }, { transport: 'sendBeacon' });
};
</script>

<style scoped>
.metadata {
  font-family: Inter, sans-serif;
  font-size: 12px;
  font-style: normal;
  font-weight: 400;
  line-height: normal;
}
</style>
